import React, { useEffect, useRef } from "react";
import {
  Field,
  FieldArrayRenderProps,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { Card, GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { MdDelete } from "react-icons/md";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setFITAccomodationDeleteIndex } from "./FITBookingSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IMealType } from "../mealtype/mealtypeModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import IconButton from "../../components/common/IconButton";
import FormSingleSuggestionField from "../../components/formFields/FormSingleSuggestionField";
import { ICurrency } from "../currency/currencyModel";
import {
  getAllActiveHotelRoomCategory,
  getHotelSpecialRate,
} from "../hotel/services/hotel.services";
import FormIncrementalSearch from "../../components/formFields/FormIncrementalSearch";
import { getAllHotelsForDropdown } from "../quotation/services/quotation.services";
import { getAllCityBySearch } from "../city/services/city.services";
import { HOTELPREFERENCE } from "../../_contstants/common";
// import FormCheckbox from "../../components/formFields/FormCheckbox";

interface ChangeWatcherProps {
  fieldArrayRenderProps: any;
  index: number;
}

const TotalChangeWatcher = ({
  fieldArrayRenderProps,
  index,
}: ChangeWatcherProps) => {
  const TotalRoom =
    fieldArrayRenderProps?.formRenderProps.valueGetter(
      `Accommodations.${index}.TotalRoom`,
    ) || 0;
  const HotelRate =
    fieldArrayRenderProps?.formRenderProps.valueGetter(
      `Accommodations.${index}.HotelRate`,
    ) || 0;

  useEffect(() => {
    fieldArrayRenderProps?.formRenderProps.onChange(
      `Accommodations.${index}.HotelAmount`,
      {
        value: (TotalRoom || 0) * (HotelRate || 0),
      },
    );
  }, [TotalRoom, HotelRate]);

  return null;
};

const FITBookingAccomodationArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const FITAccomodationDeleteIndex = useAppSelector(
    (state) => state.FITBooking.FITAccomodationDeleteIndex,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const MealTypeList = useAppSelector((state) => state.mealType.MealTypeList);
  const CurrencyList = useAppSelector((state) => state.currency.CurrencyList);

  const handleSearchHotel = (search: any, index: any) => {
    if (search) {
      fieldArrayRenderProps.handleHotelSearchChange(
        search,
        `Accommodations.${index}.hotel_options`,
        fieldArrayRenderProps.formRenderProps,
      );
    }
  };

  const handleOpenDeleteDialog = (index: number) => {
    dispatch(openDialog("fitAccomodationDeleteDialog"));
    dispatch(setFITAccomodationDeleteIndex(index));
    if (
      !fieldArrayRenderProps.value ||
      fieldArrayRenderProps?.value?.length <= 1
    ) {
      fieldArrayRenderProps?.formRenderProps.onChange("TotalAccomodation", {
        value: 0,
      });
      fieldArrayRenderProps?.formRenderProps.onChange(
        "AccommodationCurrencyID",
        {
          value: 1,
        },
      );
      fieldArrayRenderProps?.formRenderProps.onChange(
        "AccommodationConversionRate",
        {
          value: 1,
        },
      );
    }
  };

  const handleDeleteAction = React.useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      dispatch(setFITAccomodationDeleteIndex(-1));
    },
    [fieldArrayRenderProps],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        PlaceID: null,
        Hotel: "",
        CheckInDate: "",
        CheckOutDate: "",
        RoomTypeID: null,
        MealTypeID: null,
        TotalRoom: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const fetchHotelRate = async (index: number, accommodations: any) => {
    if (index >= accommodations.length) return;

    const accommodation = accommodations[index];
    if (!accommodation) return;

    const {
      Hotel,
      CheckInDate,
      RoomTypeID = 0,
      MealTypeID = 0,
    } = accommodation;
    const IsAutoRate = !isNaN(Number(Hotel));

    if (IsAutoRate && Hotel && CheckInDate && RoomTypeID && MealTypeID) {
      try {
        const res = await dispatch(
          getHotelSpecialRate({
            HotelID: Hotel,
            CheckInDate: moment(CheckInDate).format("YYYY-MM-DD"),
            MealTypeID: MealTypeID,
            RoomCategoryID: RoomTypeID,
          }),
        );

        if (res.meta?.requestStatus === "fulfilled") {
          fieldArrayRenderProps.formRenderProps.onChange(
            `Accommodations.${index}.HotelRate`,
            { value: res?.payload?.DoubleOccupancyRate || 0 },
          );
        }
      } catch (error) {
        console.error("Failed to fetch hotel rate:", error);
        fieldArrayRenderProps.formRenderProps.onChange(
          `Accommodations.${index}.HotelRate`,
          { value: 0 },
        );
      }
    }
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleAccommodationChange = (index: number, fieldName: string) => {
    const relevantFields = ["Hotel", "CheckInDate", "RoomTypeID", "MealTypeID"];
    if (!relevantFields?.includes(fieldName)) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const currentValues = fieldArrayRenderProps?.value;

    timeoutRef.current = setTimeout(() => {
      fetchHotelRate(index, currentValues);
    }, 500);
  };

  const handleHotelChange = async (index: number, e: any) => {
    const Hotel = fieldArrayRenderProps.formRenderProps.valueGetter(
      `Accommodations.${index}.Hotel`,
    );
    if (Hotel && typeof Hotel === "number") {
      const res = await dispatch(
        getAllActiveHotelRoomCategory({ HotelID: +e?.value }),
      );
      if (res.meta?.requestStatus === "fulfilled") {
        fieldArrayRenderProps.formRenderProps.onChange(
          `Accommodations.${index}.roomtype_options`,
          {
            value: res.payload || [],
          },
        );
      }
    }
  };

  const handlePlaceChange = async (index: number) => {
    const placeIF = fieldArrayRenderProps.formRenderProps.valueGetter(
      `Accommodations.${index}.PlaceID`,
    );
    const categoryID = fieldArrayRenderProps.formRenderProps.valueGetter(
      `Accommodations.${index}.HotelCategoryID`,
    );
    if (categoryID && placeIF) {
      const payload = {
        HotelCategory: `${categoryID}`,
        PlaceID: placeIF,
      };

      try {
        const res = await dispatch(getAllHotelsForDropdown(payload));
        if (res.meta?.requestStatus === "fulfilled") {
          fieldArrayRenderProps.formRenderProps.onChange(
            `Accommodations.${index}.hotel_options`,
            {
              value: res?.payload?.Data,
            },
          );
        } else {
          fieldArrayRenderProps.formRenderProps.onChange(
            `Accommodations.${index}.hotel_options`,
            {
              value: [],
            },
          );
        }
      } catch (error) {
        console.error("Failed to fetch hotel:", error);
        fieldArrayRenderProps.formRenderProps.onChange(
          `Accommodations.${index}.hotel_options`,
          {
            value: [],
          },
        );
      }
    }
  };

  const handleCitySearchChange = React.useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const result = await dispatch(getAllCityBySearch(search));
      if (result.payload) {
        formRenderProps.onChange(field, {
          value: result.payload,
        });
      }
    },
    [dispatch],
  );

  return (
    <GridLayout cols={[{ width: "75%" }, { width: "25%" }]}>
      <GridLayoutItem
        colSpan={2}
        style={{
          display: "flex",
          gap: 10,
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <Field
            id={"AccommodationCurrencyID"}
            name={"AccommodationCurrencyID"}
            label={"Currency"}
            placeholder="Currency"
            component={FormSelectionField}
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            onChange={(e) => {
              if (e?.value) {
                const conversionRate =
                  CurrencyList.find(
                    (currency: ICurrency) => currency.ID === e?.value,
                  )?.CurrencyConversion || "1";

                fieldArrayRenderProps?.formRenderProps.onChange(
                  "AccommodationConversionRate",
                  {
                    value: +conversionRate,
                  },
                );
              } else {
                fieldArrayRenderProps?.formRenderProps.onChange(
                  "AccommodationConversionRate",
                  {
                    value: 1,
                  },
                );
              }
            }}
            options={CurrencyList?.map((currency: ICurrency) => {
              return {
                value: currency.ID,
                label: currency.CurrencyName,
              };
            })}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="AccommodationConversionRate"
            label="Conversion Rate"
            placeholder="0"
            component={FormNumericTextField}
            disabled={
              !fieldArrayRenderProps.value ||
              fieldArrayRenderProps?.value?.length < 1
            }
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            min={0}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="TotalAccomodation"
            label="Total Rate"
            placeholder="0"
            component={FormNumericTextField}
            disabled={
              !fieldArrayRenderProps.value ||
              fieldArrayRenderProps?.value?.length < 1
            }
            validator={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
                ? requiredValidator
                : skipValidator
            }
            astrike={
              fieldArrayRenderProps.value &&
              fieldArrayRenderProps?.value?.length > 0
            }
            min={0}
          />
          <Field
            wrapperClassName="right-aligned-field"
            name="AccommodationMarkup"
            label="Accommodation Markup"
            placeholder="0"
            component={FormNumericTextField}
            min={0}
          />
        </div>
        {(!fieldArrayRenderProps.value ||
          fieldArrayRenderProps?.value?.length < 1) && (
          <div>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
              style={{ marginTop: 35 }}
            >
              Add Accommodation
            </Button>
          </div>
        )}
      </GridLayoutItem>
      {dialogName === "fitAccomodationDeleteDialog" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Accommodation"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(FITAccomodationDeleteIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value.map(
          (Accommodations: any, index: number) => (
            <GridLayoutItem colSpan={2} key={index}>
              <Card className="fieldarray-card">
                <GridLayout
                  style={{ marginRight: 40 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      astrike={true}
                      validator={requiredValidator}
                      name={`Accommodations.${index}.PlaceID`}
                      label="City"
                      placeholder="i.e. Ahmedabad"
                      component={FormIncrementalSearch}
                      onChange={() => {
                        handlePlaceChange(index);
                      }}
                      fetchIncrementalData={(search: any) =>
                        handleCitySearchChange(
                          search,
                          `Accommodations.${index}.place_options`,
                          fieldArrayRenderProps?.formRenderProps,
                        )
                      }
                      options={
                        fieldArrayRenderProps?.value[index].place_options?.map(
                          (item: any) => {
                            return {
                              value: item?.ID,
                              label: item?.CityName,
                            };
                          },
                        ) || []
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Accommodations.${index}.HotelCategoryID`}
                      id={"HotelCategoryID"}
                      label={"Hotel Category"}
                      placeholder="Select Category"
                      component={FormSelectionField}
                      astrike={true}
                      validator={requiredValidator}
                      onChange={() => {
                        handlePlaceChange(index);
                      }}
                      options={HOTELPREFERENCE?.map((Preference: any) => {
                        return {
                          value: +Preference?.label,
                          label: Preference?.value,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Accommodations.${index}.Hotel`}
                      label="Hotel"
                      placeholder="Hotel"
                      component={FormSingleSuggestionField}
                      astrike={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? true
                          : false
                      }
                      validator={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? requiredValidator
                          : skipValidator
                      }
                      fetchIncrementalData={(search: any) =>
                        handleSearchHotel(search, index)
                      }
                      updateOptions={(newOption: any) => {
                        const currentOptions =
                          fieldArrayRenderProps.value[index]?.hotel_options ||
                          [];
                        const updatedOptions = newOption?.value
                          ? [
                              {
                                ID: newOption.label,
                                HotelName: newOption.label,
                              },
                              ...currentOptions,
                            ]
                          : currentOptions;
                        fieldArrayRenderProps.formRenderProps.onChange(
                          `Accommodations.${index}.hotel_options`,
                          {
                            value: updatedOptions,
                          },
                        );
                      }}
                      options={
                        fieldArrayRenderProps.value[index]?.hotel_options?.map(
                          (item: any) => {
                            return {
                              value: item?.ID,
                              // value: item?.HotelName,
                              label: item?.HotelName,
                            };
                          },
                        ) || []
                      }
                      onChange={(value) => {
                        handleAccommodationChange(index, "Hotel");
                        handleHotelChange(index, value);
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Accommodations.${index}.CheckInDate`}
                      label="Check In Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                      minDate={moment().toDate()}
                      onChange={() =>
                        handleAccommodationChange(index, "CheckInDate")
                      }
                      astrike={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? true
                          : false
                      }
                      validator={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? requiredValidator
                          : skipValidator
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Accommodations.${index}.CheckOutDate`}
                      label="Check Out Date"
                      format="dd/MM/yyyy"
                      minDate={
                        fieldArrayRenderProps.formRenderProps.valueGetter(
                          `Accommodations.${index}.CheckInDate`,
                        )
                          ? moment(
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `Accommodations.${index}.CheckInDate`,
                              ),
                            ).toDate()
                          : moment().toDate()
                      }
                      component={FormDatePicker}
                      astrike={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? true
                          : false
                      }
                      validator={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? requiredValidator
                          : skipValidator
                      }
                    />
                  </GridLayoutItem>
                  {!isNaN(
                    Number(fieldArrayRenderProps?.value[index]?.Hotel),
                  ) ? (
                    <GridLayoutItem>
                      <Field
                        name={`Accommodations.${index}.RoomTypeID`}
                        label="Room Type"
                        placeholder="Room Type"
                        component={FormSelectionField}
                        astrike={
                          fieldArrayRenderProps?.value[index]?.PlaceID ||
                          fieldArrayRenderProps?.value[index]?.Hotel ||
                          fieldArrayRenderProps?.value[index]?.CheckInDate ||
                          fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                          fieldArrayRenderProps?.value[index]?.MealTypeID ||
                          fieldArrayRenderProps?.value[index]?.TotalRoom
                            ? true
                            : false
                        }
                        validator={
                          fieldArrayRenderProps?.value[index]?.PlaceID ||
                          fieldArrayRenderProps?.value[index]?.Hotel ||
                          fieldArrayRenderProps?.value[index]?.CheckInDate ||
                          fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                          fieldArrayRenderProps?.value[index]?.MealTypeID ||
                          fieldArrayRenderProps?.value[index]?.TotalRoom
                            ? requiredValidator
                            : skipValidator
                        }
                        onChange={() =>
                          handleAccommodationChange(index, "RoomTypeID")
                        }
                        // options={RoomTypeList.map((roomtype: IRoomType) => {
                        //   return {
                        //     value: roomtype?.ID,
                        //     label: roomtype?.RoomType,
                        //   };
                        // })}
                        options={
                          fieldArrayRenderProps?.value[
                            index
                          ]?.roomtype_options?.map((item: any) => {
                            return {
                              value: item?.ID,
                              label: item?.RoomCategoryName,
                            };
                          }) || []
                        }
                      />
                    </GridLayoutItem>
                  ) : (
                    <GridLayoutItem>
                      <Field
                        name={`Accommodations.${index}.RoomCategoryName`}
                        label="Room Type"
                        placeholder="Room Type"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                  )}
                  <GridLayoutItem>
                    <Field
                      name={`Accommodations.${index}.MealTypeID`}
                      label="Meal Type"
                      placeholder="Meal Type"
                      component={FormSelectionField}
                      onChange={() =>
                        handleAccommodationChange(index, "MealTypeID")
                      }
                      isAddNew={true}
                      isIndirect={true}
                      drawerName="mealtypeForm"
                      astrike={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? true
                          : false
                      }
                      validator={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.TotalRoom
                          ? requiredValidator
                          : skipValidator
                      }
                      options={MealTypeList.map((mealtype: IMealType) => {
                        return {
                          value: mealtype?.ID,
                          label: mealtype?.MealType,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem className="d-flex gap-2">
                    <Field
                      wrapperClassName="w-100 right-aligned-field"
                      name={`Accommodations.${index}.TotalRoom`}
                      label="Total Room"
                      placeholder="0"
                      max={50}
                      component={FormNumericTextField}
                      astrike={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID
                          ? true
                          : false
                      }
                      validator={
                        fieldArrayRenderProps?.value[index]?.PlaceID ||
                        fieldArrayRenderProps?.value[index]?.Hotel ||
                        fieldArrayRenderProps?.value[index]?.CheckInDate ||
                        fieldArrayRenderProps?.value[index]?.CheckOutDate ||
                        fieldArrayRenderProps?.value[index]?.RoomTypeID ||
                        fieldArrayRenderProps?.value[index]?.MealTypeID
                          ? requiredValidator
                          : skipValidator
                      }
                    />
                    <Field
                      wrapperClassName="w-100 right-aligned-field"
                      name={`Accommodations.${index}.HotelRate`}
                      label="Rate"
                      placeholder="0"
                      min={0}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem className="d-flex gap-2 justify-content-between">
                    <Field
                      wrapperClassName="w-100 right-aligned-field"
                      name={`Accommodations.${index}.HotelAmount`}
                      label="Amount"
                      placeholder="0"
                      min={0}
                      disabled
                      component={FormNumericTextField}
                    />
                    <TotalChangeWatcher
                      fieldArrayRenderProps={fieldArrayRenderProps}
                      index={index}
                    />
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(index)}
                      // disabled={fieldArrayRenderProps.value.length === 1}
                      themeColor={"error"}
                      size={"small"}
                      fillMode={"solid"}
                      type="button"
                      style={{
                        height: 30,
                        width: 30,
                        marginTop: 35,
                      }}
                      icon={MdDelete}
                      iconClassName="absolute-position"
                      iconStyle={{ fontSize: "16px" }}
                    />
                  </GridLayoutItem>
                </GridLayout>
              </Card>
            </GridLayoutItem>
          ),
        )}

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps?.value?.length > 0 && (
          <GridLayoutItem colSpan={2} style={{ textAlign: "end" }}>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Accommodation
            </Button>
          </GridLayoutItem>
        )}
    </GridLayout>
  );
};

export default FITBookingAccomodationArray;
